import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  salesAttributionService,
  type SalesAttributionFilterParams,
  type SalesAttributionFiltersResponse,
  type SalesAttributionKpiTrio,
  type SalesAttributionKpisResponse,
  type SalesAttributionOverlapResponse,
  type SalesAttributionOverlapSet as ApiOverlapSet,
  type SalesAttributionPlatformFilter,
  type SalesAttributionReconciliationResponse,
  type SalesAttributionRevenueSource,
  type SalesAttributionScorecardResponse,
  type SalesAttributionScorecardRow,
  type SalesAttributionShopifyVsGa4Response,
} from "src/services/SalesAttributionAPI";
import {
  EMPTY_SALES_ATTRIBUTION_FILTER_OPTIONS,
  EMPTY_SALES_ATTRIBUTION_METRIC,
  type SalesAttributionChannelComparisonRow,
  type SalesAttributionChannelRow,
  type SalesAttributionDateRange,
  type SalesAttributionFilterState,
  type SalesAttributionKpiEntry,
  type SalesAttributionKpis,
  type SalesAttributionMetric,
  type SalesAttributionOverlapSet,
  type SalesAttributionSeriesKey,
  type SalesAttributionTrendPoint,
  type SalesAttributionView,
} from "../utils/SalesAttributionTypes";

interface UseSalesAttributionDashboardResult {
  data: SalesAttributionView | null;
  loading: boolean;
  scorecardLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const monthBoundsOf = (anchor: string) => {
  const day = dayjs(anchor, "YYYY-MM-DD");
  const safe = day.isValid() ? day : dayjs();
  return {
    startDate: safe.startOf("month").format("YYYY-MM-DD"),
    endDate: safe.endOf("month").format("YYYY-MM-DD"),
  };
};

const metric = (
  value: number | null | undefined,
  formattedValue: string | null | undefined,
): SalesAttributionMetric =>
  formattedValue
    ? { value: Number(value ?? 0), formattedValue }
    : { ...EMPTY_SALES_ATTRIBUTION_METRIC, value: Number(value ?? 0) };

const percentChangeOf = (current: number, previous: number): number | null => {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) {
    return null;
  }
  return ((current - previous) / Math.abs(previous)) * 100;
};

const formatPercentChange = (value: number | null): string | null =>
  value === null ? null : `${value > 0 ? "+" : value < 0 ? "-" : ""}${Math.abs(value).toFixed(1)}%`;

const kpiEntry = (
  current: SalesAttributionKpiTrio | undefined,
  previous: SalesAttributionKpiTrio | undefined,
  available: boolean,
): SalesAttributionKpiEntry => {
  const value = Number(current?.value ?? 0);
  const previousValue = Number(previous?.value ?? 0);
  const percentChange = percentChangeOf(value, previousValue);
  return {
    ...metric(value, current?.formattedValue),
    previous: metric(previousValue, previous?.formattedValue),
    percentChange,
    percentChangeFormattedValue: formatPercentChange(percentChange),
    available,
  };
};

const buildKpis = (
  current: SalesAttributionKpisResponse,
  previous: SalesAttributionKpisResponse | null,
): SalesAttributionKpis => ({
  shopifyRevenue: kpiEntry(current.shopify?.revenue, previous?.shopify?.revenue, true),
  shopifyUsers: kpiEntry(current.shopify?.total_users, previous?.shopify?.total_users, true),
  ga4Revenue: kpiEntry(
    current.ga4?.revenue,
    previous?.ga4?.revenue,
    current.ga4?.available !== false,
  ),
  ga4Users: kpiEntry(
    current.ga4?.total_users,
    previous?.ga4?.total_users,
    current.ga4?.available !== false,
  ),
  googleRevenue: kpiEntry(
    current.google_ads?.revenue,
    previous?.google_ads?.revenue,
    current.google_ads?.available !== false,
  ),
  googleSpend: kpiEntry(
    current.google_ads?.spend,
    previous?.google_ads?.spend,
    current.google_ads?.available !== false,
  ),
  metaRevenue: kpiEntry(
    current.meta_ads?.revenue,
    previous?.meta_ads?.revenue,
    current.meta_ads?.available !== false,
  ),
  metaSpend: kpiEntry(
    current.meta_ads?.spend,
    previous?.meta_ads?.spend,
    current.meta_ads?.available !== false,
  ),
  untrackedRevenue: kpiEntry(
    current.invisible_channel?.not_tracked_revenue,
    previous?.invisible_channel?.not_tracked_revenue,
    true,
  ),
  untrackedShare: kpiEntry(
    current.invisible_channel?.not_tracked_share_percent,
    previous?.invisible_channel?.not_tracked_share_percent,
    true,
  ),
  untrackedOrders: kpiEntry(
    current.invisible_channel?.not_tracked_orders,
    previous?.invisible_channel?.not_tracked_orders,
    true,
  ),
});

const SERIES_FIELD: Record<SalesAttributionSeriesKey, string> = {
  shopifyOrders: "shopify_orders",
  ga4Orders: "ga4_orders",
  googleOrders: "google_ads_orders",
  metaOrders: "meta_ads_orders",
  googleOrdersShopify: "google_ads_orders_shopify",
  metaOrdersShopify: "meta_ads_orders_shopify",
  googleOrdersGA4: "google_ads_orders_ga4",
  metaOrdersGA4: "meta_ads_orders_ga4",
  shopifyRevenue: "shopify_revenue",
  ga4Revenue: "ga4_revenue",
  googleRevenue: "google_ads_revenue",
  metaRevenue: "meta_ads_revenue",
  googleSpend: "google_ads_spend",
  metaSpend: "meta_ads_spend",
};

const SERIES_KEYS = Object.keys(SERIES_FIELD) as SalesAttributionSeriesKey[];

const readTrendSeriesValue = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === "") {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildTrend = (
  response: SalesAttributionReconciliationResponse | null,
): SalesAttributionTrendPoint[] => {
  const today = dayjs().startOf("day");

  return (response?.rows ?? [])
    .filter(row => {
      const date = dayjs(row.date, "YYYY-MM-DD");
      // Stop the reconciliation series at today — do not plot future month days.
      return date.isValid() && !date.isAfter(today, "day");
    })
    .map(row => {
      const source = row as unknown as Record<string, unknown>;
      const numbers = {} as Record<SalesAttributionSeriesKey, number | null>;
      const formatted = {} as Record<SalesAttributionSeriesKey, string>;

      for (const key of SERIES_KEYS) {
        const field = SERIES_FIELD[key];
        // Keep gaps empty on the chart instead of coercing missing values to 0.
        numbers[key] = readTrendSeriesValue(source[field]);
        formatted[key] = String(source[`${field}FormattedValue`] ?? "—");
      }

      return {
        ...numbers,
        date: row.date,
        dateLabel: row.dateFormattedValue ?? row.date,
        formatted,
      };
    });
};

const buildChannelComparison = (
  response: SalesAttributionShopifyVsGa4Response | null,
): SalesAttributionChannelComparisonRow[] =>
  (response?.rows ?? []).map(row => ({
    channelId: row.channel,
    channelName: row.label ?? row.channel,
    shopifyRevenue: metric(row.shopify_revenue, row.shopify_revenueFormattedValue),
    ga4Revenue: metric(row.ga4_revenue, row.ga4_revenueFormattedValue),
  }));

const mapScorecardRow = (row: SalesAttributionScorecardRow): SalesAttributionChannelRow => ({
  channelId: row.channel,
  channelName: row.label ?? row.channel,
  spend: metric(row.spend, row.spendFormattedValue),
  platformRevenue: metric(row.platform_revenue, row.platform_revenueFormattedValue),
  attributedRevenue: metric(row.attributed_revenue, row.attributed_revenueFormattedValue),
  roas: metric(row.roas, row.roasFormattedValue),
  orders: metric(row.orders, row.ordersFormattedValue),
  impressions: metric(row.impressions, row.impressionsFormattedValue),
  clicks: metric(row.clicks, row.clicksFormattedValue),
  ctr: metric(row.ctr, row.ctrFormattedValue),
});

const mapOverlapSet = (set: ApiOverlapSet | undefined): SalesAttributionOverlapSet => ({
  googleClaims: metric(set?.google_ads_claims, set?.google_ads_claimsFormattedValue),
  metaClaims: metric(set?.meta_ads_claims, set?.meta_ads_claimsFormattedValue),
  googleOnly: metric(set?.google_only, set?.google_onlyFormattedValue),
  metaOnly: metric(set?.meta_only, set?.meta_onlyFormattedValue),
  claimedByBoth: metric(set?.claimed_by_both, set?.claimed_by_bothFormattedValue),
  overlapPercent: metric(set?.overlap_percent, set?.overlap_percentFormattedValue),
});

const buildFilterOptions = (response: SalesAttributionFiltersResponse | null) => {
  if (!response) {
    return EMPTY_SALES_ATTRIBUTION_FILTER_OPTIONS;
  }
  return {
    utmCombinations: response.utm_combinations ?? [],
    platforms: response.platforms ?? [],
    campaigns: response.campaigns ?? [],
  };
};

const asPlatformFilter = (value: string): SalesAttributionPlatformFilter | null => {
  if (value === "all" || value === "google_ads" || value === "meta_ads") {
    return value;
  }
  return null;
};

const toFilterParams = (
  filters: SalesAttributionFilterState,
): SalesAttributionFilterParams => ({
  utm_source: filters.utmSource || null,
  utm_medium: filters.utmMedium || null,
  utm_campaign: filters.utmCampaign || null,
  platform: asPlatformFilter(filters.platform),
  campaign: filters.campaign || null,
});

const dedupe = (values: Array<string | undefined | null>): string[] =>
  Array.from(new Set(values.filter((entry): entry is string => Boolean(entry))));

export const useSalesAttributionDashboard = (
  dateRange: SalesAttributionDateRange,
  filters: SalesAttributionFilterState,
  revenueSource: SalesAttributionRevenueSource,
): UseSalesAttributionDashboardResult => {
  const { i18n } = useTranslation();
  const lang = getApiLangFromI18n(i18n);

  const [data, setData] = useState<SalesAttributionView | null>(null);
  const [loading, setLoading] = useState(true);
  const [scorecardLoading, setScorecardLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const mainRequestIdRef = useRef(0);
  const scorecardRequestIdRef = useRef(0);
  const latestScorecardRef = useRef<SalesAttributionScorecardResponse | null>(null);

  const refetch = useCallback(() => setReloadToken(token => token + 1), []);

  const reportingMonth = useMemo(
    () => monthBoundsOf(dateRange.endDate || dateRange.startDate),
    [dateRange.endDate, dateRange.startDate],
  );
  const comparisonMonth = useMemo(
    () => monthBoundsOf(dateRange.startDate || dateRange.endDate),
    [dateRange.startDate, dateRange.endDate],
  );

  const filterParams = useMemo(() => toFilterParams(filters), [filters]);
  const utmOnlyParams = useMemo(
    () => ({
      utm_source: filterParams.utm_source,
      utm_medium: filterParams.utm_medium,
      utm_campaign: filterParams.utm_campaign,
    }),
    [filterParams],
  );

  useEffect(() => {
    const requestId = mainRequestIdRef.current + 1;
    mainRequestIdRef.current = requestId;
    const isStale = () => mainRequestIdRef.current !== requestId;

    const load = async () => {
      setLoading(true);
      setError(null);

      const options = { lang };
      const [
        kpisResult,
        previousKpisResult,
        reconciliationResult,
        channelResult,
        overlapResult,
        filtersResult,
      ] = await Promise.allSettled([
        salesAttributionService.fetchKpis(
          reportingMonth.startDate,
          reportingMonth.endDate,
          options,
        ),
        salesAttributionService.fetchKpis(
          comparisonMonth.startDate,
          comparisonMonth.endDate,
          options,
        ),
        salesAttributionService.fetchReconciliation(
          reportingMonth.startDate,
          reportingMonth.endDate,
          filterParams,
          options,
        ),
        salesAttributionService.fetchShopifyVsGa4ByChannel(
          reportingMonth.startDate,
          reportingMonth.endDate,
          utmOnlyParams,
          options,
        ),
        salesAttributionService.fetchPlatformOverlap(
          reportingMonth.startDate,
          reportingMonth.endDate,
          options,
        ),
        salesAttributionService.fetchFilters(
          reportingMonth.startDate,
          reportingMonth.endDate,
          options,
        ),
      ]);

      if (isStale()) {
        return;
      }

      if (kpisResult.status === "rejected") {
        setData(null);
        setError(
          kpisResult.reason instanceof Error
            ? kpisResult.reason.message
            : "Sales attribution data could not be loaded.",
        );
        setLoading(false);
        return;
      }

      const valueOf = <TResponse,>(
        result: PromiseSettledResult<TResponse>,
      ): TResponse | null => (result.status === "fulfilled" ? result.value : null);

      const kpis = kpisResult.value;
      const previousKpis = valueOf(previousKpisResult);
      const reconciliation = valueOf(reconciliationResult);
      const channels = valueOf(channelResult);
      const overlap = valueOf(overlapResult) as SalesAttributionOverlapResponse | null;
      const filterOptionsResponse = valueOf(filtersResult);
      const scorecardResponse = latestScorecardRef.current;

      setData({
        kpis: buildKpis(kpis, previousKpis),
        availability: {
          ga4: reconciliation?.availability?.ga4 ?? kpis.ga4?.available !== false,
          googleAds:
            reconciliation?.availability?.google_ads ?? kpis.google_ads?.available !== false,
          metaAds:
            reconciliation?.availability?.meta_ads ?? kpis.meta_ads?.available !== false,
        },
        trend: buildTrend(reconciliation),
        channelComparison: buildChannelComparison(channels),
        channelRows: (scorecardResponse?.rows ?? []).map(mapScorecardRow),
        scorecardTotal: scorecardResponse?.total
          ? mapScorecardRow(scorecardResponse.total)
          : null,
        overlap: {
          orders: mapOverlapSet(overlap?.orders),
          revenue: mapOverlapSet(overlap?.revenue),
        },
        filterOptions: buildFilterOptions(filterOptionsResponse),
        warnings: dedupe([
          ...(kpis.warnings ?? []),
          ...(channels?.warnings ?? []),
          ...(scorecardResponse?.warnings ?? []),
          ...(filterOptionsResponse?.warnings ?? []),
        ]),
        notes: dedupe([
          ...(reconciliation?.notes ?? []),
          ...(channels?.notes ?? []),
          ...(scorecardResponse?.notes ?? []),
          ...(overlap?.notes ?? []),
          ...(filterOptionsResponse?.notes ?? []),
        ]),
      });
      setLoading(false);
    };

    load().catch(caught => {
      if (isStale()) {
        return;
      }
      setError(
        caught instanceof Error
          ? caught.message
          : "Sales attribution data could not be loaded.",
      );
      setData(null);
      setLoading(false);
    });
  }, [
    reportingMonth.startDate,
    reportingMonth.endDate,
    comparisonMonth.startDate,
    comparisonMonth.endDate,
    filterParams,
    utmOnlyParams,
    lang,
    reloadToken,
  ]);

  // Scorecard-only effect: re-fires when revenueSource changes without
  // triggering the 6 other calls above.
  useEffect(() => {
    const requestId = scorecardRequestIdRef.current + 1;
    scorecardRequestIdRef.current = requestId;
    const isStale = () => scorecardRequestIdRef.current !== requestId;

    const loadScorecard = async () => {
      setScorecardLoading(true);
      try {
        const scorecardResponse = (await salesAttributionService.fetchChannelScorecard(
          reportingMonth.startDate,
          reportingMonth.endDate,
          revenueSource,
          { lang },
        )) as SalesAttributionScorecardResponse | null;

        if (isStale()) {
          return;
        }

        latestScorecardRef.current = scorecardResponse;
        setData(prev => {
          if (!prev) {
            return prev;
          }
          const scorecardWarnings = scorecardResponse?.warnings ?? [];
          const scorecardNotes = scorecardResponse?.notes ?? [];
          return {
            ...prev,
            channelRows: (scorecardResponse?.rows ?? []).map(mapScorecardRow),
            scorecardTotal: scorecardResponse?.total
              ? mapScorecardRow(scorecardResponse.total)
              : null,
            warnings: dedupe([
              ...prev.warnings.filter(w => !scorecardWarnings.includes(w)),
              ...scorecardWarnings,
            ]),
            notes: dedupe([
              ...prev.notes.filter(n => !scorecardNotes.includes(n)),
              ...scorecardNotes,
            ]),
          };
        });
      } finally {
        if (!isStale()) {
          setScorecardLoading(false);
        }
      }
    };

    loadScorecard().catch(() => {
      if (!isStale()) {
        setScorecardLoading(false);
      }
    });
  }, [
    reportingMonth.startDate,
    reportingMonth.endDate,
    revenueSource,
    lang,
    reloadToken,
  ]);

  return { data, loading, scorecardLoading, error, refetch };
};
